import {
  StockStatuses,
  type ProductCardProps
} from '@dcxwam/dcx-wam-ui-foundations'
import { getMatchedVariant, HistoryFilter } from './filter'
import { type Stocks, getStockLevelStatus, stockStatusMap } from './stock'
import { ProductDataVariant } from '../../../../services/product/product-data'
import { isMinQuantityExceedsStock } from '../../../../utils/quantity-selector'

type ColorSelector = {
  variants: ProductDataVariant[]
  stocks: Stocks
  historyFilters: HistoryFilter[]
  dotsVisibility: boolean
  maxColorsPerView?: number
}

const getColorVariants = (variants: ProductDataVariant[], stocks: Stocks) =>
  Object.values<NonNullable<ProductCardProps['colorSelector']>>(
    (variants || []).reduce(
      (acc, variant) => {
        const nuanceCode = variant?.nuance?.code
        const stockLevelStatus = getStockLevelStatus(variant, stocks)

        let currentStock = stockLevelStatus && stockStatusMap[stockLevelStatus]
        if (nuanceCode && currentStock === StockStatuses.OutOfStock) {
          const hasStockByNuanceCode = variants
            .filter((v) => v.nuance?.code === nuanceCode)
            .some((v) => {
              const resolvedStockLevelStatus =
                (v.globalArticleCode &&
                  stocks?.[v.globalArticleCode]?.stockLevelStatus) ||
                v.stock?.stockLevel

              const variantMinOrder = v.minimumOrderQuantity ?? 0
              const variantStockNum: number | undefined =
                (v.globalArticleCode
                  ? stocks?.[v.globalArticleCode]?.stockLevel
                  : undefined) ??
                v?.stock?.stockLevel ??
                undefined
              const minExceedsVariantStock = isMinQuantityExceedsStock(
                variantMinOrder,
                variantStockNum
              )

              return (
                !!resolvedStockLevelStatus &&
                stockStatusMap[resolvedStockLevelStatus] !==
                  StockStatuses.OutOfStock &&
                !minExceedsVariantStock
              )
            })
          currentStock = hasStockByNuanceCode
            ? StockStatuses.InStock
            : StockStatuses.OutOfStock
        }

        // Override to OutOfStock if minimumOrderQuantity exceeds available stock
        if (currentStock && currentStock !== StockStatuses.OutOfStock) {
          const variantStockLevel: number | undefined =
            (variant.globalArticleCode
              ? stocks?.[variant.globalArticleCode]?.stockLevel
              : undefined) ??
            variant?.stock?.stockLevel ??
            undefined
          if (
            isMinQuantityExceedsStock(
              variant.minimumOrderQuantity,
              variantStockLevel
            )
          ) {
            currentStock = StockStatuses.OutOfStock
          }
        }

        return nuanceCode
          ? {
              ...acc,
              [nuanceCode]: {
                colorLabel: variant.nuance?.description,
                value: nuanceCode,
                colorHex: variant.nuance?.hexCode,
                stock: currentStock,
                'aria-label': variant.nuance?.description
              }
            }
          : acc
      },
      {} as Record<string, NonNullable<ProductCardProps['colorSelector']>>
    )
  )

export const getColorSelector = ({
  variants,
  stocks,
  historyFilters = [],
  dotsVisibility = false,
  maxColorsPerView
}: ColorSelector) => {
  const matchedVariant = getMatchedVariant(variants, historyFilters)

  return dotsVisibility
    ? {
        ...(typeof maxColorsPerView === 'number' ? { maxColorsPerView } : {}),
        currentValue: matchedVariant?.nuance?.code,
        colorVariants: getColorVariants(variants, stocks)
      }
    : undefined
}

export const getVariantsWithQty = (
  variantList: ProductDataVariant[],
  target: HTMLInputElement
) => {
  return variantList.reduce(
    (acc: Record<string, number>, item: ProductDataVariant) => {
      if (item.globalArticleCode) {
        acc[item.globalArticleCode] = Number(target.value)
      }
      return acc
    },
    {}
  )
}
